import { LINK_TRANSFORMER } from '#pplx/frontend/composer-sdk/internal/link/linkTransformer.js';
import { $getSelection, $isRangeSelection, $isTextNode, COLLABORATION_TAG, HISTORIC_TAG, } from 'lexical';
/**
 * Registers markdown shortcuts for links only.
 * This handles the keyboard shortcut for creating links when typing [text](url).
 * This is a simplified version of the registerLinkMarkdownShortcuts from @lexical/markdown.
 * It only supports links and does not support other markdown features.
 * @returns Cleanup that stops converting typed links for this editor.
 */
export function registerLinkMarkdownShortcuts({ editor, }) {
    const trigger = LINK_TRANSFORMER.trigger;
    const regExp = LINK_TRANSFORMER.regExp;
    const replace = LINK_TRANSFORMER.replace;
    if (!trigger || !replace) {
        return () => { };
    }
    return editor.registerUpdateListener(({ tags, dirtyLeaves, editorState, prevEditorState }) => {
        // Ignore updates from collaboration and undo/redo
        if (tags.has(COLLABORATION_TAG) || tags.has(HISTORIC_TAG)) {
            return;
        }
        // If editor is composing, wait for confirmation
        if (editor.isComposing()) {
            return;
        }
        const selection = editorState.read($getSelection);
        const prevSelection = prevEditorState.read($getSelection);
        // We expect selection to be a collapsed range and not match previous one (as we want
        // to trigger transforms only as user types)
        if (!$isRangeSelection(prevSelection) ||
            !$isRangeSelection(selection) ||
            !selection.isCollapsed() ||
            selection.is(prevSelection)) {
            return;
        }
        const anchorKey = selection.anchor.key;
        const anchorOffset = selection.anchor.offset;
        const anchorNode = editorState._nodeMap.get(anchorKey);
        if (!$isTextNode(anchorNode) ||
            !dirtyLeaves.has(anchorKey) ||
            (anchorOffset !== 1 && anchorOffset > prevSelection.anchor.offset + 1)) {
            return;
        }
        editor.update(() => {
            const textContent = anchorNode.getTextContent();
            // Check if the trigger character was just typed
            if (textContent[anchorOffset - 1] !== trigger) {
                return;
            }
            // Get the text up to the cursor
            const textUpToCursor = textContent.slice(0, anchorOffset);
            const match = regExp.exec(textUpToCursor);
            if (!match) {
                return;
            }
            // The match should end at the cursor position
            const matchEnd = match.index + match[0].length;
            if (matchEnd !== anchorOffset) {
                return;
            }
            // Split the text node if necessary
            let targetNode = anchorNode;
            if (match.index > 0) {
                const splitNodes = anchorNode.splitText(match.index, anchorOffset);
                if (splitNodes[1]) {
                    targetNode = splitNodes[1];
                }
            }
            else if (anchorOffset < textContent.length) {
                const splitNodes = anchorNode.splitText(anchorOffset);
                if (splitNodes[0]) {
                    targetNode = splitNodes[0];
                }
            }
            // Apply the replacement
            replace(targetNode, match);
        });
    });
}
//# sourceMappingURL=registerLinkMarkdownShortcuts.js.map